import pb from '../lib/pocketbase';
import { brevoEmailService } from './brevoEmailService';
import { EmailNotificationJob, EmailNotificationEvent } from '../types';

class NotificationTriggerService {
  /**
   * Trigger notifications for a specific event
   */
  async triggerEvent(
    partnerId: string,
    event: EmailNotificationEvent,
    eventData: any
  ): Promise<void> {
    try {
      console.log(`🔔 Triggering notifications for event: ${event}, partner: ${partnerId}`);

      // Get all enabled jobs for this partner
      const jobs = await pb.collection('email_notification_jobs').getFullList<EmailNotificationJob>({
        filter: `partner_id = "${partnerId}" && enabled = true`,
      });

      if (jobs.length === 0) {
        console.log(`No enabled notification jobs found for partner: ${partnerId}`);
        return;
      }

      // Filter jobs that subscribe to this specific event
      const relevantJobs = jobs.filter((job: EmailNotificationJob) => {
        const subscriptions = typeof job.event_subscriptions === 'string'
          ? JSON.parse(job.event_subscriptions)
          : job.event_subscriptions;
        return subscriptions.includes(event);
      });

      if (relevantJobs.length === 0) {
        console.log(`No jobs subscribed to event: ${event}`);
        return;
      }

      console.log(`Found ${relevantJobs.length} job(s) subscribed to ${event}`);

      // Send emails for each job
      for (const job of relevantJobs) {
        try {
          const recipients = typeof job.recipient_emails === 'string'
            ? JSON.parse(job.recipient_emails)
            : job.recipient_emails;

          console.log(`Sending ${event} notification to:`, recipients, `(Job: ${job.job_name})`);

          await this.sendEventEmail(event, recipients, eventData);
        } catch (error) {
          console.error(`Failed to send notification for job ${job.id}:`, error);
        }
      }

      console.log(`✅ Successfully processed ${relevantJobs.length} notification job(s)`);
    } catch (error) {
      console.error(`Failed to trigger notifications for event ${event}:`, error);
    }
  }

  /**
   * Send email based on event type
   */
  private async sendEventEmail(
    event: EmailNotificationEvent,
    recipients: string[],
    data: any
  ): Promise<void> {
    const recipientList = recipients.map(email => ({ email, name: email.split('@')[0] }));

    switch (event) {
      case 'campaign_created':
        await brevoEmailService.sendCampaignCreatedEmail(recipientList, data);
        break;

      case 'campaign_updated':
        await brevoEmailService.sendCampaignUpdatedEmail(recipientList, data);
        break;

      case 'campaign_status_updates':
        await brevoEmailService.sendCampaignStatusEmail(recipientList, data);
        break;

      case 'promo_code_requested':
        await brevoEmailService.sendPromoCodeRequestEmail(recipientList, data);
        break;

      case 'advertisement_created':
        await brevoEmailService.sendAdvertisementCreatedEmail(recipientList, data);
        break;

      case 'advertisement_approved':
        await brevoEmailService.sendAdApprovalEmail(
          recipientList[0].email,
          recipientList[0].name || 'Partner',
          data.ad_name || data.ad_title,
          true
        );
        break;

      case 'advertisement_rejected':
        await brevoEmailService.sendAdApprovalEmail(
          recipientList[0].email,
          recipientList[0].name || 'Partner',
          data.ad_name || data.ad_title,
          false,
          data.rejection_reason
        );
        break;

      default:
        console.warn(`Unknown event type: ${event}`);
    }
  }
}

export const notificationTriggerService = new NotificationTriggerService();
